import React, { useState, useEffect } from 'react';
import { Vote, Shield, Lock, Users, CheckSquare, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../config/supabase';

interface Candidato {
  id: string;
  nome: string;
  matricula: string;
  setor: string;
  votos: number;
  foto_url?: string;
}

interface Eleicao {
  id: string;
  titulo: string;
  empresa_nome: string;
  empresa_slug: string;
  senha_votacao: string;
  status: 'ativa' | 'encerrada';
  total_votos: number;
}

const VotacaoPublica: React.FC = () => {
  const [eleicao, setEleicao] = useState<Eleicao | null>(null);
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para votação
  const [cpfEleitor, setCpfEleitor] = useState('');
  const [senhaVotacao, setSenhaVotacao] = useState('');
  const [candidatoSelecionado, setCandidatoSelecionado] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [votoRegistrado, setVotoRegistrado] = useState(false);

  // Obter slug da empresa da URL
  const getEmpresaSlug = () => {
    const path = window.location.pathname;
    const slug = path.split('/').pop();
    return slug || '';
  };

  // Função para obter foto do candidato (mesma lógica do sistema principal)
  const obterFotoCandidato = (candidato: Candidato): string | null => {
    if (!candidato.foto_url) return null;
    
    const match = candidato.foto_url.match(/\/photos_candidate\/(\d+)_(\d+)\.jpg/);
    if (!match) return null;
    
    const [, matricula, ano] = match;
    const storageKey = `foto_candidato_${matricula}_${ano}`;
    
    const base64 = localStorage.getItem(storageKey);
    if (base64) {
      return base64;
    }
    
    return null;
  };

  // Carregar dados da eleição
  const carregarEleicao = async () => {
    try {
      setLoading(true);
      const empresaSlug = getEmpresaSlug();
      
      if (!empresaSlug) {
        setError('URL inválida. Empresa não encontrada.');
        return;
      }

      console.log('🔍 Buscando eleição para empresa:', empresaSlug);

      // Buscar eleição ativa para esta empresa
      const { data: eleicoes, error: eleicaoError } = await supabase
        .from('cp_eleicoes')
        .select('*')
        .eq('empresa_slug', empresaSlug)
        .eq('status', 'ativa')
        .single();

      if (eleicaoError) {
        console.error('❌ Erro ao carregar eleição:', eleicaoError);
        setError('Eleição não encontrada ou não está ativa.');
        return;
      }

      if (!eleicoes) {
        setError('Nenhuma eleição ativa encontrada para esta empresa.');
        return;
      }

      console.log('✅ Eleição encontrada:', eleicoes);
      setEleicao(eleicoes);

      // Buscar candidatos desta eleição
      const { data: candidatosData, error: candidatosError } = await supabase
        .from('cp_candidatos')
        .select('*')
        .eq('eleicao_id', eleicoes.id)
        .eq('ativo', true)
        .order('nome');

      if (candidatosError) {
        console.error('❌ Erro ao carregar candidatos:', candidatosError);
        setError('Erro ao carregar candidatos.');
        return;
      }

      console.log('✅ Candidatos carregados:', candidatosData);
      setCandidatos(candidatosData || []);

    } catch (err) {
      console.error('❌ Erro geral:', err);
      setError('Erro ao carregar dados da eleição.');
    } finally {
      setLoading(false);
    }
  };

  // Registrar voto
  const registrarVoto = async () => {
    if (!eleicao || !candidatoSelecionado) return;

    const cpfLimpo = cpfEleitor.replace(/\D/g, '');
    
    if (!cpfLimpo || cpfLimpo.length !== 11) {
      alert('CPF inválido! Digite apenas os 11 números.');
      return;
    }
    
    if (senhaVotacao !== eleicao.senha_votacao) {
      alert('Senha incorreta!');
      return;
    }

    try {
      setSubmitting(true);

      // Verificar se já votou (usando hash do CPF para manter anonimato)
      const cpfHash = btoa(cpfLimpo + eleicao.id);
      
      const { data: votosExistentes, error: verificarError } = await supabase
        .from('cp_votos')
        .select('id')
        .eq('eleicao_id', eleicao.id)
        .eq('cpf_hash', cpfHash)
        .single();

      if (verificarError && verificarError.code !== 'PGRST116') {
        console.error('❌ Erro ao verificar voto:', verificarError);
        throw verificarError;
      }

      if (votosExistentes) {
        alert('Este CPF já registrou um voto nesta eleição!');
        return;
      }

      // Registrar voto
      const { error: votoError } = await supabase
        .from('cp_votos')
        .insert({
          eleicao_id: eleicao.id,
          candidato_id: candidatoSelecionado,
          cpf_hash: cpfHash,
          timestamp: new Date().toISOString()
        });

      if (votoError) {
        console.error('❌ Erro ao registrar voto:', votoError);
        throw votoError;
      }

      // Atualizar contador de votos do candidato
      const candidato = candidatos.find(c => c.id === candidatoSelecionado);
      if (candidato) {
        const { error: updateError } = await supabase
          .from('cp_candidatos')
          .update({ votos: candidato.votos + 1 })
          .eq('id', candidatoSelecionado);

        if (updateError) {
          console.error('❌ Erro ao atualizar votos:', updateError);
        }
      }

      // Atualizar total de votos da eleição
      const { error: eleicaoUpdateError } = await supabase
        .from('cp_eleicoes')
        .update({ total_votos: eleicao.total_votos + 1 })
        .eq('id', eleicao.id);

      if (eleicaoUpdateError) {
        console.error('❌ Erro ao atualizar total de votos:', eleicaoUpdateError);
      }

      setVotoRegistrado(true);
      alert('Voto registrado com sucesso! O voto é secreto e não pode ser rastreado.');

      // Limpar campos
      setCpfEleitor('');
      setSenhaVotacao('');
      setCandidatoSelecionado(null);

      // Recarregar dados para atualizar contadores
      setTimeout(() => {
        carregarEleicao();
        setVotoRegistrado(false);
      }, 2000);

    } catch (err) {
      console.error('❌ Erro ao registrar voto:', err);
      alert('Erro ao registrar voto. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Carregar dados ao montar componente
  useEffect(() => {
    carregarEleicao();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando eleição...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Eleição Não Encontrada</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-sm text-gray-600">
            Verifique se a URL está correta ou entre em contato com a administração.
          </p>
        </div>
      </div>
    );
  }

  if (!eleicao) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Vote className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Eleição Não Disponível</h2>
          <p className="text-yellow-700 mb-4">
            Não há eleições ativas no momento.
          </p>
          <p className="text-sm text-gray-600">
            Aguarde o início da eleição ou entre em contato com a administração.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Sistema de Votação */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-4">
                <img src="/logo/logoEhsFacil.png" alt="CIPA FÁCIL EHS" className="h-12 w-auto mr-3" />
                <Vote className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {eleicao.titulo}
              </h2>
              <p className="text-xl text-gray-600 mb-2">
                {eleicao.empresa_nome}
              </p>
              <p className="text-gray-600">
                Sistema de Votação Online - 100% Secreto e Anônimo
              </p>
            </div>

            {/* Alertas de Segurança */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="font-bold text-yellow-800 mb-1">
                    Sistema de Voto Secreto
                  </h3>
                  <p className="text-sm text-yellow-700">
                    O CPF é usado apenas para evitar duplicação. Não há registro de qual candidato cada eleitor votou.
                    O voto é 100% secreto e anônimo conforme exigido pela NR-05.
                  </p>
                </div>
              </div>
            </div>

            {/* Formulário de Votação */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Autenticação */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Identificação do Eleitor
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CPF (apenas números)
                    </label>
                    <input
                      type="text"
                      value={cpfEleitor}
                      onChange={(e) => setCpfEleitor(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="00000000000"
                      maxLength={11}
                      disabled={votoRegistrado}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha do Sistema
                    </label>
                    <input
                      type="password"
                      value={senhaVotacao}
                      onChange={(e) => setSenhaVotacao(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Digite a senha fornecida"
                      disabled={votoRegistrado}
                    />
                  </div>
                </div>
              </div>

              {/* Lista de Candidatos */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Selecione seu Candidato
                </h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {candidatos.map(candidato => (
                    <label
                      key={candidato.id}
                      className={`flex items-center p-3 bg-white rounded-lg cursor-pointer hover:bg-blue-50 transition-colors ${
                        candidatoSelecionado === candidato.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                      } ${votoRegistrado ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="candidato"
                        value={candidato.id}
                        checked={candidatoSelecionado === candidato.id}
                        onChange={() => setCandidatoSelecionado(candidato.id)}
                        className="mr-3"
                        disabled={votoRegistrado}
                      />
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-shrink-0">
                          {(() => {
                            const fotoUrl = obterFotoCandidato(candidato);
                            return fotoUrl ? (
                              <img 
                                src={fotoUrl} 
                                alt={candidato.nome}
                                className="w-12 h-12 object-cover rounded-full"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-gray-500" />
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{candidato.nome}</div>
                          <div className="text-sm text-gray-600">
                            Mat. {candidato.matricula} - {candidato.setor}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Botão Confirmar Voto */}
            <div className="text-center mt-6">
              <button
                onClick={registrarVoto}
                disabled={!cpfEleitor || !senhaVotacao || !candidatoSelecionado || submitting || votoRegistrado}
                className={`px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto ${
                  cpfEleitor && senhaVotacao && candidatoSelecionado && !submitting && !votoRegistrado
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckSquare className="w-5 h-5" />
                )}
                {votoRegistrado ? 'Voto Registrado!' : 'Confirmar Voto'}
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600">
            Sistema de Votação CIPA - Conforme NR-05 Vigente
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Desenvolvido com segurança e transparência
          </p>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Desenvolvido por <strong>MM Solutec</strong>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Site: <a href="https://mmsolutec.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://mmsolutec.com/</a>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              © 2025 MMSolutec. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VotacaoPublica;
